import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { token, useThemeObserver } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Text, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import {
	WithModernizeNav4GlobalChangeboardingActive,
	WithModernizeNav4SpaceSettingsChangeboardingActive,
} from '@confluence/onboarding-helpers/entry-points/hooks/useModernizeChangeboardingActive.ts';

import type { TourStepComponent } from '../../hooks/useCombinedSegments.tsx';
import { CrossIconCloseButton } from '../../common/CrossIconCloseButton.tsx';
import { useSpotlightActions } from '../../hooks/useSpotlightActions.ts';

import nav4GlobalSpotlightImage from './assets/nav4GlobalSpotlightImage.svg';
import nav4GlobalSpotlightImageDark from './assets/nav4GlobalSpotlightImageDark.svg';
import nav4SpaceSpotlightImage from './assets/nav4SpaceSpotlightImage.svg';
import nav4SpaceSpotlightImageDark from './assets/nav4SpaceSpotlightImageDark.svg';

const COMMUNITY_POST_LINK =
	'https://support.atlassian.com/navigation/docs/what-is-the-new-navigation-in-confluence/';
const NAV4_GLOBAL_SPOTLIGHT_SOURCE = 'nav4GlobalSpotlight';

const Nav4GlobalSpotlight: TourStepComponent = ({
	onNext,
	onBack,
	onDismiss,
	progress,
	messageType = 'engagement',
}) => {
	const { Spotlight } = useChoreographerAssets();
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { actions, actionsBeforeElement } = useSpotlightActions({
		progress,
		onNext,
		onBack,
		source: NAV4_GLOBAL_SPOTLIGHT_SOURCE,
	});

	return (
		<WithModernizeNav4GlobalChangeboardingActive>
			<Spotlight
				actions={actions}
				actionsBeforeElement={actionsBeforeElement}
				dialogPlacement="right top"
				dialogWidth={338}
				heading={formatMessage(globalSpotlight.heading)}
				headingAfterElement={
					<CrossIconCloseButton onClose={onDismiss} source={NAV4_GLOBAL_SPOTLIGHT_SOURCE} />
				}
				image={isDarkMode ? nav4GlobalSpotlightImageDark : nav4GlobalSpotlightImage}
				messageId="nav4-spotlight-0"
				messageType={messageType}
				shouldWatchTarget
				target="nav4-spotlight-0"
				targetBgColor={token('elevation.surface')}
				targetRadius={4}
			>
				{formatMessage(globalSpotlight.body, {
					a: (chunks: React.ReactNode) => (
						<Anchor
							onClick={() => {
								createAnalyticsEvent({
									type: 'sendUIEvent',
									data: {
										source: NAV4_GLOBAL_SPOTLIGHT_SOURCE,
										action: 'clicked',
										actionSubject: 'link',
										actionSubjectId: 'exploreNav4',
									},
								}).fire();
							}}
							href={COMMUNITY_POST_LINK}
							target="_blank"
							xcss={linkStyles}
						>
							{chunks}
						</Anchor>
					),
				})}
			</Spotlight>
		</WithModernizeNav4GlobalChangeboardingActive>
	);
};

const Nav4CollapseSpotlight: TourStepComponent = ({
	onNext,
	onBack,
	onDismiss,
	progress,
	messageType = 'engagement',
}) => {
	const { Spotlight } = useChoreographerAssets();

	const { formatMessage } = useIntl();

	const { actions, actionsBeforeElement } = useSpotlightActions({
		progress,
		onNext,
		onBack,
		source: 'nav4CollapseSpotlight',
	});

	return (
		<Spotlight
			actions={actions}
			actionsBeforeElement={actionsBeforeElement}
			dialogPlacement="right top"
			dialogWidth={338}
			heading={formatMessage(collapseSpotlight.heading)}
			headingAfterElement={
				<CrossIconCloseButton onClose={onDismiss} source="nav4CollapseSpotlight" />
			}
			messageId="nav4-spotlight-1"
			messageType={messageType}
			shouldWatchTarget
			target="nav4-spotlight-1"
			targetBgColor={token('elevation.surface')}
			targetRadius={4}
		>
			{formatMessage(collapseSpotlight.body)}
		</Spotlight>
	);
};

const Nav4SpaceSettingsSpotlight: TourStepComponent = ({
	onNext,
	onBack,
	onDismiss,
	progress,
	messageType = 'engagement',
}) => {
	const { Spotlight } = useChoreographerAssets();
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	const { formatMessage } = useIntl();

	const { actions, actionsBeforeElement } = useSpotlightActions({
		progress,
		onNext,
		onBack,
		source: 'nav4SpaceSettingsSpotlight',
	});

	return (
		<WithModernizeNav4SpaceSettingsChangeboardingActive>
			<Spotlight
				actions={actions}
				actionsBeforeElement={actionsBeforeElement}
				dialogPlacement="right top"
				dialogWidth={338}
				heading={formatMessage(spaceSettingsSpotlight.heading)}
				headingAfterElement={
					<CrossIconCloseButton onClose={onDismiss} source="nav4SpaceSettingsSpotlight" />
				}
				image={isDarkMode ? nav4SpaceSpotlightImageDark : nav4SpaceSpotlightImage}
				messageId="nav4-spotlight-2"
				messageType={messageType}
				shouldWatchTarget
				target="nav4-spotlight-2"
				targetBgColor={token('elevation.surface')}
				targetRadius={4}
			>
				{formatMessage(spaceSettingsSpotlight.body, {
					strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
				})}
			</Spotlight>
		</WithModernizeNav4SpaceSettingsChangeboardingActive>
	);
};

const linkStyles = xcss({
	color: 'color.text.inverse',
	textDecoration: 'underline',
	':hover': {
		color: 'color.text.inverse',
	},
	':visited': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':active': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':focus': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':visited:active': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
});

const globalSpotlight = defineMessages({
	heading: {
		id: 'modernize-changeboarding.nav4-global-spotlight.heading',
		defaultMessage: 'Find everything you need in the sidebar',
		description:
			'Title of onboarding message describing the new global navigation menu at the top of the sidebar.',
	},
	body: {
		id: 'modernize-changeboarding.nav4-global-spotlight.body',
		defaultMessage:
			'Parts of the <a>navigation</a> have moved to the sidebar, which you can customize to your unique needs. ',
		description:
			'Body of a tutorial message describing the new global navigation menu at the top of the sidebar.',
	},
});

const collapseSpotlight = defineMessages({
	heading: {
		id: 'modernize-changeboarding.nav4-collapse-spotlight.heading',
		defaultMessage: 'Show or hide the sidebar',
		description: 'Title of onboarding message describing the new sidebar collapse button',
	},
	body: {
		id: 'modernize-changeboarding.nav4-collapse-spotlight.body',
		defaultMessage: 'Select this icon to expand or collapse the sidebar.',
		description: 'Body of a tutorial message describing the new sidebar collapse button',
	},
});

const spaceSettingsSpotlight = defineMessages({
	heading: {
		id: 'modernize-changeboarding.nav4-space-settings-spotlight.heading',
		defaultMessage: 'Quick access to settings and more',
		description: 'Title of onboarding message describing how to locate space settings',
	},
	body: {
		id: 'modernize-changeboarding.nav4-space-settings-spotlight.body',
		defaultMessage:
			'Find <strong>Space settings</strong> in <strong>More actions</strong> (<strong>•••</strong>), where you can manage permissions, access, and content, and access Premium features.',
		description: 'Body of a tutorial message describing how to locate space settings',
	},
});

export const Spotlights = {
	Nav4GlobalSpotlight,
	Nav4CollapseSpotlight,
	Nav4SpaceSettingsSpotlight,
};
